import { notFound } from "@tanstack/react-router";
import { buildHref, matchRoute } from "@softmax/web-addresses";
import { requireGameContext, requireGameResource } from "@/features/game/data";
import { type PreviewSearch } from "./front-door/front-door";
import { readFrontDoor } from "./front-door/front-door.data";
import { readFrontDoorViewer } from "./front-door/viewer.data";
import { previewStartContent } from "./front-door/start-content.data";
import { previewContent } from "./preview.data";

type PreviewRead = { href: string; signal?: AbortSignal | undefined };

function frontDoorUrl(href: string): URL {
  const url = new URL(href, window.location.origin);
  const route = matchRoute(url.pathname);

  if (
    route.name !== "game.home" &&
    route.name !== "game.league" &&
    route.name !== "game.preview"
  )
    throw notFound();

  return url;
}

function searchOf(url: URL): PreviewSearch {
  const search: Record<string, string | readonly string[] | undefined> = {};

  for (const [key, value] of url.searchParams) {
    const previous = search[key];
    search[key] =
      previous === undefined
        ? value
        : Array.isArray(previous)
          ? [...previous, value]
          : [previous, value];
  }

  return search;
}

export async function getFrontDoor({ href, signal }: PreviewRead) {
  const url = frontDoorUrl(href);
  const context = await requireGameResource({ href, signal });
  const page = await readFrontDoor({ context, search: searchOf(url), signal });

  if (!page) throw notFound();

  return page;
}

export async function getPreviewDetails({ href, signal }: PreviewRead) {
  frontDoorUrl(href);
  const context = await requireGameResource({ href, signal });
  const address = [context.address.slug, context.league?.requestedSlug]
    .filter(Boolean)
    .join("/");
  const content = await previewContent({ slug: address, context, signal });

  if (!content) throw notFound();

  return content;
}

export async function getPreviewViewer({ href, signal }: PreviewRead) {
  const url = frontDoorUrl(href);
  const context = await requireGameResource({ href, signal });
  return readFrontDoorViewer({ context, search: searchOf(url), signal });
}

export async function getPreviewRedirect({ href, signal }: PreviewRead) {
  const url = new URL(href, window.location.origin);

  if (
    url.origin !== window.location.origin ||
    matchRoute(url.pathname).name !== "legacyPreview"
  )
    throw notFound();

  const address = url.pathname.replace(/^\/preview\//, "");
  const context = await requireGameContext({
    href: `/${address}/preview${url.search}`,
    signal,
  });
  const content = await previewStartContent({ address, context, signal });

  if (!content) throw notFound();

  return buildHref({
    name: "game.preview",
    ...content.route,
    search: url.searchParams,
  });
}
